// View models of the Claude support (AD-34: decisions here, React only renders): the card after a shot and on its page
// (Dominik 22.10.2026, boards "Claude …": thinking, taking long, the suggestion with old → new, adopted with undo, an
// error with one action), the row at the next shot, the setup line without a key, the chat's chips, whether and how a
// proposal can be adopted, the texts of failures.
import type { BeanItem, ClaudeMessage, ShotItem } from '@brewcore/app-contract';
import type { CommandInput } from '../core/commands.js';
import { reasonText, t, type Lang } from '../core/i18n.js';
import { stopModeOf } from '../core/recipe.js';
import type { ScreenContext } from '../core/screens/context.js';
import { formatAuto, shotMs, whenText } from '../core/screens/format.js';
import {
  adoptedText,
  aiChanges,
  changeLabel,
  changesCommand,
  changeText,
  undoCommand,
  valueText,
  type AiChange,
} from './changes.js';
import {
  KEY_ERRORS,
  type AssistantView,
  type BeanLookupView,
  type GrinderView,
  type ShotAiView,
} from './assistant.js';

export interface ApplyView {
  /** "Mahlgrad 2,5 → 2,4 (feiner)", one per line */
  rows: string[];
  applied: boolean;
  label: string;
  enabled: boolean;
  /** why it cannot be adopted right now */
  hint: string | null;
  changes: AiChange[];
  command: CommandInput | null;
}

/**
 * What a message of Claude would change now (old → new), and whether this user may adopt it: the machine
 * controllable, every value one this user may change (the catalog's rights), and the bean of the shot still in the
 * grinder. An adopted proposal shows what it set. Null when there is nothing to change.
 */
export function applyView(
  ctx: ScreenContext,
  shot: ShotItem,
  message: ClaudeMessage,
  currentBean: Pick<BeanItem, 'id' | 'name'> | null,
): ApplyView | null {
  if (message.role !== 'assistant' || !message.changes?.length) return null;
  if (message.applied)
    return {
      rows: message.changes.map((c) => adoptedText(c, ctx.lang, ctx.catalog)),
      applied: true,
      label: ctx.tr('aiApplied'),
      enabled: false,
      hint: null,
      changes: [],
      command: null,
    };
  const changes = aiChanges(message.changes, {
    value: ctx.value,
    catalog: ctx.catalog,
    stopMode: stopModeOf(ctx.value, ctx.scale),
  });
  if (!changes.length) return null;
  let hint: string | null = null;
  if (shot.beanId && currentBean && currentBean.id !== shot.beanId)
    hint = ctx.tr('aiApplyOtherBean', { bean: currentBean.name });
  else if (!ctx.controllable) hint = ctx.tr('aiApplyOffline');
  else if (!changes.every((c) => ctx.canEdit(c.id))) hint = ctx.tr('aiApplyRights');
  return {
    rows: changes.map((c) => changeText(c, ctx.lang, ctx.catalog)),
    applied: false,
    label: ctx.tr('aiApply'),
    enabled: hint === null,
    hint,
    changes,
    command: changesCommand(changes),
  };
}

// ------------------------------------------------------------------------------------------------ the card

/** after this long the card says the suggestion will come later (board "Claude: Zustände", LinkedIn's pattern) */
export const LONG_MS = 20_000;
/** the shots Claude reads with the one talked about (ClaudePrompt.SHOTS in the API, this one included) */
const READ_SHOTS = 8;

export type ClaudeCardState = 'none' | 'thinking' | 'long' | 'error' | 'done' | 'applied';

export interface ClaudeCardRow {
  id: string;
  label: string;
  from: string;
  to: string;
}

export interface ClaudeCardView {
  state: ClaudeCardState;
  /** thinking: what Claude reads; long: that the suggestion comes later */
  line: string | null;
  /** done: the finding and the request (Claude's heading; null on a suggestion of the first version) */
  head: string | null;
  /** done: the reason with the user's numbers (model output, rendered as text only) */
  text: string | null;
  /** done: each change old → new */
  rows: ClaudeCardRow[];
  /** done: what it is based on, with the (i) that Claude can be wrong */
  source: string | null;
  /** done: adopting (null when nothing would change now) */
  apply: ApplyView | null;
  /** applied: what the next shot runs with */
  appliedText: string | null;
  /** applied in this session: the values from before as one set */
  undo: CommandInput | null;
  /** error: the sentence and its one action */
  error: { text: string; action: 'retry' | 'settings' } | null;
  /** the suggestion's place in the conversation (adopting, undo) */
  index: number;
}

export interface ClaudeCardInput {
  ctx: ScreenContext;
  shot: ShotItem;
  assistant: Pick<AssistantView, 'loaded' | 'account'>;
  ai: ShotAiView;
  currentBean: Pick<BeanItem, 'id' | 'name'> | null;
  /** earlier shots of the bean in the app's history (what Claude reads, until its answer says itself) */
  earlier: number;
  /** after the shot ("Ausblenden" keeps the card away) or on the shot's own page (the answer shows there) */
  place: 'done' | 'shot';
  /** the proposal whose command is out (ApplyBox's useApply) */
  sending?: ApplyView | null;
}

const EMPTY: Omit<ClaudeCardView, 'state'> = {
  line: null,
  head: null,
  text: null,
  rows: [],
  source: null,
  apply: null,
  appliedText: null,
  undo: null,
  error: null,
  index: -1,
};

/** Earlier shots of the shot's bean (all without one) in the app's history, as many as Claude reads with it. */
export function earlierShots(shot: ShotItem, items: readonly ShotItem[]): number {
  const at = shotMs(shot);
  const n = items.filter(
    (s) => s.id !== shot.id && shotMs(s) < at && (!shot.beanId || s.beanId === shot.beanId),
  ).length;
  return Math.min(n, READ_SHOTS - 1);
}

/** "Claude liest diesen und 5 frühere Bezüge …" */
export function readingText(lang: Lang, n: number): string {
  return n <= 0 ? t(lang, 'aiReading0') : n === 1 ? t(lang, 'aiReading1') : t(lang, 'aiReadingN', { n });
}

/** "Aus diesem und 5 früheren Bezügen" */
export function sourceText(lang: Lang, n: number): string {
  return n <= 0 ? t(lang, 'aiSource0') : n === 1 ? t(lang, 'aiSource1') : t(lang, 'aiSourceN', { n });
}

/** The suggestion of a conversation (it comes first) and where it is. */
export function suggestionOf(
  ai: Pick<ShotAiView, 'thread'>,
): { message: ClaudeMessage; index: number } | null {
  const messages = ai.thread?.messages ?? [];
  const index = messages.findIndex((m) => m.role === 'assistant' && m.kind === 'suggestion');
  const message = index >= 0 ? messages[index] : undefined;
  return message ? { message, index } : null;
}

/** "Mahlgrad 2,3, Dosis 18,5 g": what an adopted proposal set, without the direction (the heading says it). */
function setText(ctx: ScreenContext, message: ClaudeMessage): string {
  return (message.changes ?? [])
    .map(
      (c) =>
        `${changeLabel(c.param, ctx.lang, ctx.catalog)} ${valueText(c.param, c.to, ctx.lang, ctx.catalog)}`,
    )
    .join(', ');
}

/**
 * The Claude card: nothing before the account came, without a key (unless a request just said why) or when nothing
 * was asked (Dominik 23.10.2026: no "Vorschlag holen" box, it is on no board), what Claude reads while it thinks,
 * after 20 s that it comes later, a failure with one action, the suggestion with old → new and "Übernehmen", adopted
 * with "Rückgängig" (in this session).
 */
export function claudeCardView(i: ClaudeCardInput): ClaudeCardView {
  const { ctx, ai } = i;
  const none: ClaudeCardView = { state: 'none', ...EMPTY };
  if (!i.assistant.loaded) return none;
  if (ai.busy === 'suggest') {
    if (i.place === 'done' && ai.hidden) return none;
    const long = ai.since !== null && ctx.now - ai.since >= LONG_MS;
    return {
      state: long ? 'long' : 'thinking',
      ...EMPTY,
      line: long ? ctx.tr('aiLong') : readingText(ctx.lang, i.earlier),
    };
  }
  if (ai.error && ai.errorFor !== 'chat')
    return {
      state: 'error',
      ...EMPTY,
      error: {
        text: failureText(ctx.lang, ai.error),
        action: KEY_ERRORS.has(ai.error) ? 'settings' : 'retry',
      },
    };
  if (!i.assistant.account?.key) return none;
  if (i.place === 'done' && ai.hidden) return none;
  const found = suggestionOf(ai);
  if (!found) return none;
  const { message, index } = found;
  if (message.applied && !i.sending)
    return {
      state: 'applied',
      ...EMPTY,
      index,
      appliedText:
        (message.changes?.length ?? 0) > 0
          ? ctx.tr('aiAppliedNext', { changes: setText(ctx, message) })
          : null,
      undo: ai.undo?.index === index ? undoCommand(ai.undo.changes) : null,
    };
  const apply = i.sending ?? applyView(ctx, i.shot, message, i.currentBean);
  const live = apply && !apply.applied ? apply : null;
  return {
    state: 'done',
    ...EMPTY,
    index,
    head: message.head ?? null,
    text: message.text,
    rows: (live?.changes ?? []).map((c) => ({
      id: c.id,
      label: changeLabel(c.id, ctx.lang, ctx.catalog),
      from: valueText(c.id, c.from, ctx.lang, ctx.catalog),
      to: valueText(c.id, c.to, ctx.lang, ctx.catalog),
    })),
    source: sourceText(ctx.lang, message.basis ?? i.earlier),
    apply: live,
  };
}

// ------------------------------------------------------------------------------------------------ the next shot

export interface NextClaudeView {
  state: 'pending' | 'applied';
  /** "Claude schlägt Mahlgrad 2,3 statt 2,5 vor", or what was set ("Mahlgrad 2,3 von Claude, zum Bezug von …") */
  text: string;
  /** "zum sauren Bezug von heute 07:25", or why it cannot be adopted now */
  sub: string | null;
  apply: ApplyView | null;
  undo: CommandInput | null;
  index: number;
}

/**
 * Bezug → "Nächster Bezug" (board "Claude: beim nächsten Bezug"): Claude's suggestion on the newest shot while it is
 * still open, with "Übernehmen"; adopted here or on the card, "… von Claude" with "Rückgängig" in this session.
 */
export function nextClaudeView(
  ctx: ScreenContext,
  shot: ShotItem,
  assistant: Pick<AssistantView, 'loaded' | 'account'>,
  ai: ShotAiView,
  currentBean: Pick<BeanItem, 'id' | 'name'> | null,
  sending: ApplyView | null = null,
): NextClaudeView | null {
  if (!assistant.loaded || !assistant.account?.key || ai.busy) return null;
  const found = suggestionOf(ai);
  if (!found) return null;
  const { message, index } = found;
  const when = whenText(shotMs(shot), ctx.lang, ctx.now);
  if (message.applied && !sending) {
    if (ai.undo?.index !== index) return null;
    return {
      state: 'applied',
      text: ctx.tr('aiNextApplied', { changes: setText(ctx, message), when }),
      sub: null,
      apply: null,
      undo: undoCommand(ai.undo.changes),
      index,
    };
  }
  const apply = sending ?? applyView(ctx, shot, message, currentBean);
  if (!apply || apply.applied) return null;
  const changes = apply.changes
    .map(
      (c) =>
        `${changeLabel(c.id, ctx.lang, ctx.catalog)} ${ctx.tr('aiInstead', {
          to: valueText(c.id, c.to, ctx.lang, ctx.catalog),
          from: valueText(c.id, c.from, ctx.lang, ctx.catalog),
        })}`,
    )
    .join(', ');
  const from =
    shot.rating === 1 ? 'aiNextFromSour' : shot.rating === 3 ? 'aiNextFromBitter' : ('aiNextFrom' as const);
  return {
    state: 'pending',
    text: ctx.tr('aiNextPending', { changes }),
    sub: apply.enabled || sending ? ctx.tr(from, { when }) : apply.hint,
    apply,
    undo: null,
    index,
  };
}

// ------------------------------------------------------------------------------------------------ without a key

/**
 * Without a key in the account (Dominik 22.10.2026): one quiet line after a rated shot, "Einrichten" and "Nicht jetzt"
 * (gone for good, kept in the account); otherwise nothing of Claude shows.
 */
export function setupOfferShown(
  view: Pick<AssistantView, 'loaded' | 'account'>,
  rating: number | null | undefined,
): boolean {
  return (
    view.loaded &&
    !!view.account &&
    !view.account.key &&
    view.account.setupDismissed !== true &&
    (rating === 1 || rating === 2 || rating === 3)
  );
}

// ------------------------------------------------------------------------------------------------ chat

/**
 * Answers to tap instead of an empty field (board "Claude: Weiter fragen", Fitbit's pattern): why this change and a
 * smaller step when it proposes one, the other taste when the shot was sour or bitter; one already sent is gone.
 */
export function chatChips(lang: Lang, shot: ShotItem, messages: readonly ClaudeMessage[]): string[] {
  const found = suggestionOf({ thread: { shotId: shot.id, messages: [...messages] } });
  const changes = (found?.message.changes?.length ?? 0) > 0;
  const chips = [
    ...(changes ? [t(lang, 'aiChipWhy'), t(lang, 'aiChipSmaller')] : []),
    ...(shot.rating === 1 ? [t(lang, 'aiChipBitter')] : shot.rating === 3 ? [t(lang, 'aiChipSour')] : []),
  ];
  const sent = new Set(messages.filter((m) => m.role === 'user').map((m) => m.text.trim()));
  return chips.filter((c) => !sent.has(c));
}

/** A failure in plain words: what happened and what to do (reason codes of the API, offline of this device). */
export function failureText(lang: Lang, code: string): string {
  switch (code) {
    case 'offline':
      return t(lang, 'aiErr_offline');
    case 'claude_no_key':
      return t(lang, 'aiErr_noKey');
    case 'claude_key_invalid':
      return t(lang, 'aiErr_key');
    case 'claude_key_format':
      return t(lang, 'aiErr_format');
    case 'claude_credit':
      return t(lang, 'aiErr_credit');
    case 'claude_model':
      return t(lang, 'aiErr_model');
    case 'claude_rate_limited':
      return t(lang, 'aiErr_rateLimited');
    case 'limit':
      return t(lang, 'aiErr_limit');
    case 'claude_refused':
      return t(lang, 'aiErr_refusal');
    case 'claude_failed':
      return t(lang, 'aiErr_empty');
    case 'claude_unavailable':
      return t(lang, 'aiErr_unavailable');
    default:
      return reasonText(lang, code);
  }
}

/** Only web links from Claude's citations become links (never javascript: or data:). */
export function safeSource(url: string): { href: string; host: string } | null {
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    return { href: u.href, host: u.hostname.replace(/^www\./, '') };
  } catch {
    return null;
  }
}

export interface GrinderInfoView {
  /** what Claude found out (null: not looked up yet) */
  info: string | null;
  sources: { href: string; host: string }[];
  busy: boolean;
  /** heading of the folded text: "Angaben zur Mühle", or "Claude sucht …" while looking */
  title: string;
  /** "Nachschlagen" (nothing known yet) or "Erneut nachschlagen"; none while looking */
  action: { label: string; again: boolean } | null;
  error: string | null;
}

/**
 * Bezug → Bohnen & Mühle, under the grinder's name and scale (Dominik, 19.10.2026: the grinder does not belong in the
 * Claude card): what Claude knows about this grinder for the account. Nothing without a grinder name, before the
 * account's Claude settings came, or without a key (Dominik 22.10.2026: without a key nothing of Claude shows).
 */
export function grinderInfoView(
  lang: Lang,
  name: string,
  assistant: Pick<AssistantView, 'loaded' | 'account'>,
  g: GrinderView,
): GrinderInfoView | null {
  if (!name.trim() || !assistant.loaded || !assistant.account?.key) return null;
  const busy = g.busy;
  const title = t(lang, busy ? 'aiGrinderSearching' : 'aiGrinderKnown');
  const same = g.data && g.data.name === name.trim();
  const info = same ? (g.data?.info ?? null) : null;
  return {
    info,
    sources: same ? (g.data?.sources ?? []).map(safeSource).filter((x) => x !== null) : [],
    busy,
    title,
    action: busy ? null : { label: t(lang, info ? 'aiGrinderAgain' : 'aiGrinderLookUp'), again: !!info },
    error: g.error && !busy ? failureText(lang, g.error) : null,
  };
}

export interface BeanInfoView {
  status: 'none' | 'found' | 'missed';
  busy: boolean;
  /** the folded facts' row: "Angaben zur Bohne", or "Claude sucht …" while looking */
  title: string;
  /** the roast as a word and its place on the scale from light (0) to dark (1) */
  roast: { label: string; value: string; at: number } | null;
  rows: { key: 'varieties' | 'recipe' | 'notes'; label: string; value: string }[];
  sources: { href: string; host: string }[];
  /** one sentence before the lookup or after a miss */
  text: string | null;
  /** "Bohne nachschlagen", or "Erneut nachschlagen" (again) after a lookup; none while looking */
  action: { label: string; again: boolean } | null;
  ends: { light: string; dark: string };
  error: string | null;
}

/**
 * Board "Eine Bohne" (Dominik 25.10.2026: "ja, zeilen und nur anzeigen"): what Claude found out about the bean, as rows
 * (roast with its scale, varieties, the roaster's espresso recipe, tasting notes), only what the search found for sure.
 * Nothing before the account's Claude settings came or without a key, like the grinder.
 */
export function beanInfoView(
  lang: Lang,
  assistant: Pick<AssistantView, 'loaded' | 'account'>,
  b: BeanLookupView,
  beanId: string,
): BeanInfoView | null {
  if (!assistant.loaded || !assistant.account?.key) return null;
  const d = b.data && b.data.beanId === beanId ? b.data : null;
  const status = d?.status ?? 'none';
  const f = status === 'found' ? (d?.facts ?? null) : null;
  const busy = b.busy;
  const rows: BeanInfoView['rows'] = [];
  if (f?.varieties) rows.push({ key: 'varieties', label: t(lang, 'aiBeanVarieties'), value: f.varieties });
  const recipe = [
    typeof f?.dose === 'number' ? `${formatAuto(f.dose, lang)} g` : '',
    typeof f?.ratio === 'number' ? `1:${formatAuto(f.ratio, lang)}` : '',
    typeof f?.temp === 'number' ? `${formatAuto(f.temp, lang)} °C` : '',
  ].filter(Boolean);
  if (recipe.length) rows.push({ key: 'recipe', label: t(lang, 'aiBeanRecipe'), value: recipe.join(' · ') });
  if (f?.notes) rows.push({ key: 'notes', label: t(lang, 'aiBeanNotes'), value: f.notes });
  const roast =
    typeof f?.roast === 'number' && f.roast >= 1 && f.roast <= 5
      ? {
          label: t(lang, 'aiBeanRoast'),
          value: t(lang, `aiRoast${f.roast}` as 'aiRoast1'),
          // from 10 % to 90 %: the dot stays on the track
          at: 0.1 + ((f.roast - 1) / 4) * 0.8,
        }
      : null;
  return {
    status,
    busy,
    title: t(lang, busy ? 'aiBeanSearching' : 'aiBeanKnown'),
    roast,
    rows,
    sources: f ? (d?.sources ?? []).map(safeSource).filter((x) => x !== null) : [],
    text: busy
      ? null
      : status === 'missed'
        ? t(lang, 'aiBeanMissed')
        : status === 'none'
          ? t(lang, 'aiBeanPitch')
          : null,
    action: busy
      ? null
      : { label: t(lang, status === 'none' ? 'aiBeanLookUp' : 'aiGrinderAgain'), again: status !== 'none' },
    ends: { light: t(lang, 'aiRoast1'), dark: t(lang, 'aiRoast5') },
    error: b.error && !busy ? failureText(lang, b.error) : null,
  };
}
